import { useEffect, useState } from "react";
import {
  ArrowRight,
  Building2,
  Home,
  Mail,
  MapPin,
  Phone,
  User,
} from "lucide-react";
import { isContactSatisfied } from "@autopic/shared";
import { Button } from "../../components/Button";
import { StepHeader } from "../../components/StepHeader";
import { TextField } from "../../components/Field";
import { track } from "../../analytics/analytics";
import { useSession } from "../../store/sessionStore";
import type { StepProps } from "../stepTypes";
import { getDetailsContactFieldVisibility } from "./detailsContactFields";
import s from "../steps.module.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+0-9()\s-]{7,}$/;
const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

/** Collect name, email and mobile together before reviewing the booking. */
export function DetailsStep({ onNext, saveLead }: StepProps) {
  const config = useSession((st) => st.config)!;
  const strategy = config.flow.contactStrategy;
  const contact = useSession((st) => st.contact);
  const setContact = useSession((st) => st.setContact);
  const setNotes = useSession((st) => st.setNotes);
  const notes = useSession((st) => st.notes);
  const bookingPostcode = useSession((st) => st.postcode);
  const contactAddress = useSession((st) => st.contactAddress);
  const contactCity = useSession((st) => st.contactCity);
  const contactPostcode = useSession((st) => st.contactPostcode);
  const contactInfoFields = useSession((st) => st.contactInfoFields);
  const setContactAddress = useSession((st) => st.setContactAddress);
  const setContactCity = useSession((st) => st.setContactCity);
  const setContactPostcode = useSession((st) => st.setContactPostcode);
  const editingFromSummary = useSession((st) => st.returnStepIndex !== null);

  const [name, setName] = useState(contact.name ?? "");
  const [email, setEmail] = useState(contact.email ?? "");
  const [phone, setPhone] = useState(contact.phone ?? "");
  const [address, setAddress] = useState(contactAddress ?? "");
  const [city, setCity] = useState(contactCity ?? "");
  const [postcode, setLocalPostcode] = useState(
    contactPostcode ?? bookingPostcode ?? "",
  );
  const [localNotes, setLocalNotes] = useState(notes ?? "");
  const [touched, setTouched] = useState(false);

  const required = new Set(contactInfoFields?.required ?? []);
  const optional = new Set(contactInfoFields?.optional ?? []);
  const phoneRequired = required.has("contact_number");
  const emailRequired = required.has("contact_email");
  const addressRequired = required.has("contact_address");
  const cityRequired = required.has("contact_city");
  const postcodeRequired = required.has("contact_postcode");
  const showAddress = addressRequired || optional.has("contact_address");
  const showCity = cityRequired || optional.has("contact_city");
  const showPostcode = postcodeRequired || optional.has("contact_postcode");
  const { contactCapturedEarlier, showEmail, showPhone } =
    getDetailsContactFieldVisibility({
      steps: config.flow.steps,
      strategy,
      contact,
      requiredFields: contactInfoFields?.required ?? [],
      editingFromSummary,
    });

  // Keep the final-details form in sync if the customer goes back and changes
  // the earlier contact step before returning here.
  useEffect(() => {
    setEmail(contact.email ?? "");
    setPhone(contact.phone ?? "");
  }, [contact.email, contact.phone]);

  const emailValid = !email || EMAIL_RE.test(email.trim());
  const phoneValid = !phone || PHONE_RE.test(phone.trim());
  const nameValid = name.trim().length >= 2;
  const addressValid = !addressRequired || address.trim().length >= 2;
  const cityValid = !cityRequired || city.trim().length >= 2;
  const postcodeValid = !postcodeRequired || UK_POSTCODE.test(postcode.trim());

  const candidate = {
    name: name.trim(),
    email: emailValid && email ? email.trim() : undefined,
    phone: phoneValid && phone ? phone.trim() : undefined,
  };
  const contactOk =
    isContactSatisfied(strategy, candidate) &&
    (!phoneRequired || !!candidate.phone) &&
    (!emailRequired || !!candidate.email);
  const valid =
    nameValid &&
    emailValid &&
    phoneValid &&
    contactOk &&
    addressValid &&
    cityValid &&
    postcodeValid;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    setContact({
      name: candidate.name,
      email: candidate.email,
      phone: candidate.phone,
    });
    setContactAddress(address.trim() || null);
    setContactCity(city.trim() || null);
    setContactPostcode(postcode.trim().toUpperCase() || null);
    setNotes(localNotes.trim() || null);
    track("details_entered", {});
    saveLead("details");
    onNext();
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <StepHeader
        kicker="Almost there"
        title={config.copy.detailsTitle}
        subtitle={
          contactCapturedEarlier && !editingFromSummary
            ? "Just add your name and any final details for the garage."
            : "Your contact details are used to complete the garage booking."
        }
        display={false}
      />
      <div className={s.stack}>
        <TextField
          label="Full name"
          placeholder="Jane Smith"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={touched && !nameValid ? "Please enter your name" : null}
          icon={<User size={19} />}
          autoComplete="name"
        />
        {showEmail && (
          <TextField
            label={
              emailRequired || strategy === "email" || strategy === "both"
                ? "Email address"
                : "Email address (optional)"
            }
            type="email"
            inputMode="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={
              touched && !emailValid
                ? "Enter a valid email"
                : touched && emailRequired && !candidate.email
                  ? "Email is required by the garage"
                  : null
            }
            icon={<Mail size={19} />}
            autoComplete="email"
          />
        )}
        {showPhone && (
          <TextField
            label={
              phoneRequired || strategy !== "email"
                ? "Mobile number"
                : "Mobile number (optional)"
            }
            type="tel"
            inputMode="tel"
            placeholder="07123 456789"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={
              touched && !phoneValid
                ? "Enter a valid phone number"
                : touched && phoneRequired && !candidate.phone
                  ? "Mobile number is required by the garage"
                  : null
            }
            icon={<Phone size={18} />}
            autoComplete="tel"
          />
        )}
        {showAddress && (
          <TextField
            label={addressRequired ? "Address" : "Address (optional)"}
            placeholder="12 High Street"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            error={
              touched && !addressValid
                ? "Address is required by the garage"
                : null
            }
            icon={<Home size={18} />}
            autoComplete="street-address"
          />
        )}
        {showCity && (
          <TextField
            label={cityRequired ? "Town or city" : "Town or city (optional)"}
            placeholder="Winchester"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            error={
              touched && !cityValid
                ? "Town or city is required by the garage"
                : null
            }
            icon={<Building2 size={18} />}
            autoComplete="address-level2"
          />
        )}
        {showPostcode && (
          <TextField
            label={postcodeRequired ? "Postcode" : "Postcode (optional)"}
            placeholder="SO23 8TJ"
            value={postcode}
            onChange={(e) => setLocalPostcode(e.target.value.toUpperCase())}
            error={
              touched && !postcodeValid ? "Enter a valid UK postcode" : null
            }
            icon={<MapPin size={18} />}
            autoComplete="postal-code"
          />
        )}
        <TextField
          label="Anything we should know? (optional)"
          placeholder="e.g. warning light on, prefer a morning drop-off"
          value={localNotes}
          onChange={(e) => setLocalNotes(e.target.value)}
          maxLength={300}
        />
        {touched && strategy === "either" && !contactOk && (
          <p
            role="alert"
            style={{ color: "var(--ap-danger)", fontSize: 13, fontWeight: 600 }}
          >
            {phoneRequired
              ? "Please provide the mobile number required by the garage."
              : "Please provide an email or a phone number."}
          </p>
        )}
      </div>
      <div className={s.actions}>
        <Button
          type="submit"
          block
          disabled={!valid}
          trailingIcon={<ArrowRight size={18} />}
        >
          Review booking
        </Button>
      </div>
    </form>
  );
}
